/**
 * DecisionFlow — PlansLandingPage
 * ui/src/components/tenant-admin/PlansLandingPage.jsx
 *
 * Replaces PlanWizardPanel as the /admin/plans landing surface.
 * Spec authority: comp_policies_spec_v2_5.md §4.4, §19
 *
 * Layout: Segment-first grouping (default). View toggles: Segment · Role · Business Unit · Status · Flat List
 * Filters: BU · Segment · Role · Status · Search · Show Withdrawn toggle
 * Row click → /admin/plans/:plan_id (PlanDetailView)
 *
 * API:
 *   GET    /api/v1/plans?fiscal_period_id={id}[&plan_status=withdrawn]
 *   GET    /api/v1/roles
 *   GET    /api/v1/segments
 *   GET    /api/v1/business-units
 *   DELETE /api/v1/plans/{id}                   — §19 States 1+2 hard delete
 *   POST   /api/v1/plans/{id}/withdraw           — §19 State 4a soft delete
 *
 * Architecture:
 *   - useAuth() for user — token never in props
 *   - apiGet / apiPost / apiDelete from client
 *   - tenant_id never in request body
 *   - BU toggle disabled when all plans have null business_unit_id (Polaris case)
 *   - AD-42: NO plan-level approve action. Plan status is derived from component approval.
 *
 * Props:
 *   period        — { fiscal_period_id, period_code, period_label } | null
 *   isLocked      — bool (optional, falls back to period.period_status !== 'open')
 *   initialRoleId — int (optional, seeds role filter from CRL Design Plan button)
 *   onPlanClick   — fn(planId) (optional, overrides navigate)
 *
 * S685: §19 lifecycle action menu added. Withdrawn filter toggle added.
 *       AD-42 inline approve button removed from PlanRow + GroupBlock.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { apiGet, apiPost, apiDelete } from '../../api/client';
import { Lock } from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// §4.6: sessionStorage key for filter persistence — plan-list-specific.
// Client-side only — no server affinity, compatible with stateless backends (Cloud Run etc.).
const PLAN_LIST_FILTER_KEY = "plan-list-filters-v1";

const PAGE_SIZE = 25;

const VIEW_MODES = [
  { key: 'segment',  label: 'Segment' },
  { key: 'role',     label: 'Role' },
  { key: 'bu',       label: 'Business Unit' },
  { key: 'status',   label: 'Status' },
  { key: 'flat',     label: 'Flat List' },
];

// §4.6 sort options
const SORT_OPTIONS = [
  { value: 'modified_desc', label: 'Modified (newest)' },
  { value: 'modified_asc',  label: 'Modified (oldest)' },
  { value: 'name_asc',      label: 'Plan name (A–Z)'   },
  { value: 'name_desc',     label: 'Plan name (Z–A)'   },
  { value: 'status',        label: 'Status / Attention' },
];

const STATUS_OPTIONS = [
  { value: '',                 label: 'All statuses' },
  { value: 'draft',            label: 'Draft' },
  { value: 'pending_approval', label: 'Pending Approval' },
  { value: 'approved',         label: 'Approved' },
  { value: 'withdrawn',        label: 'Withdrawn' },
  { value: 'archived',         label: 'Archived' },
];

const STATUS_COLORS = {
  draft:            'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  pending_approval: 'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
  approved:         'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
  withdrawn:        'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
  archived:         'bg-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))]',
};

// Fix 5 — moved outside component: was re-created as a new object on every render.
// §4.6 sort priority for status/attention sort: draft > pending_approval > approved > withdrawn
const STATUS_ATTENTION_ORDER = { draft: 0, pending_approval: 1, approved: 2, withdrawn: 3 };

// Fix 6 — debounce delay for search input and sessionStorage writes (ms)
const DEBOUNCE_MS = 300;

const STATUS_LABELS = {
  draft:            'Draft',
  pending_approval: 'Pending Approval',
  approved:         'Approved',
  withdrawn:        'Withdrawn',
  archived:         'Archived',
};

// ---------------------------------------------------------------------------
// Shared CSS
// ---------------------------------------------------------------------------

const inputClass =
  'rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] ' +
  'text-[rgb(var(--color-text))] px-3 py-1.5 text-tok-sm focus:outline-none ' +
  'focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';

const btnPrimary =
  'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';

const btnDanger =
  'rounded-lg bg-[rgb(var(--color-danger))] px-3 py-1.5 text-tok-sm font-medium ' +
  'text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';

// ---------------------------------------------------------------------------
// StatusPill
// ---------------------------------------------------------------------------

function StatusPill({ status }) {
  const colorClass = STATUS_COLORS[status] ?? STATUS_COLORS.draft;
  const label = STATUS_LABELS[status] ?? status?.replace(/_/g, ' ') ?? '—';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// PlanActionMenu — §19 lifecycle actions per plan state
//
// State derivation from API response fields (all returned by plans_router S684):
//   component_count  — int
//   assignment_count — int
//   any_submitted    — bool
//   plan_status      — string (derived by backend per AD-42)
//
// Actions per §19:
//   draft, component_count=0               → Delete
//   draft, components, not submitted        → Delete
//   draft, components, any_submitted=true   → (no delete — must withdraw submission first via component)
//   approved, assignment_count=0            → Withdraw
//   approved, assignment_count>0            → (no action — reassign first)
//   withdrawn                               → Restore (CA-2 scope — S685 deferred, shown disabled)
// ---------------------------------------------------------------------------

function PlanActionMenu({ plan, onDelete, onWithdraw, actioning }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isActioning = actioning === plan.plan_id;
  const status = plan.plan_status;
  const componentCount  = plan.component_count  ?? 0;
  const assignmentCount = plan.assignment_count ?? 0;
  const anySubmitted    = plan.any_submitted    ?? false;

  // Derive available actions per §19
  const canDelete =
    status === 'draft' &&
    !anySubmitted; // States 1 and 2 — covers component_count=0 and components not submitted

  const canWithdraw =
    status === 'approved' &&
    assignmentCount === 0; // State 4a only

  const isWithdrawn = status === 'withdrawn';

  // No actions available for this plan
  const hasActions = canDelete || canWithdraw || isWithdrawn;
  if (!hasActions) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={isActioning}
        className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))] disabled:opacity-40"
        title="Plan actions"
        aria-label="Plan actions"
      >
        {isActioning ? (
          <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-1 w-44 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-lg py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {canDelete && (
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(plan); }}
              className="w-full text-left px-4 py-2 text-tok-sm text-[rgb(var(--color-danger))] hover:bg-[rgb(var(--color-danger))]/5"
            >
              Delete Plan
            </button>
          )}
          {anySubmitted && status === 'draft' && (
            <div className="px-4 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Withdraw component submission first to enable delete.
            </div>
          )}
          {canWithdraw && (
            <button
              type="button"
              onClick={() => { setOpen(false); onWithdraw(plan); }}
              className="w-full text-left px-4 py-2 text-tok-sm text-[rgb(var(--color-warning))] hover:bg-[rgb(var(--color-warning))]/5"
            >
              Withdraw Plan
            </button>
          )}
          {status === 'approved' && assignmentCount > 0 && (
            <div className="px-4 py-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
              {assignmentCount} position{assignmentCount !== 1 ? 's' : ''} assigned — reassign all before withdrawing.
            </div>
          )}
          {isWithdrawn && (
            <button
              type="button"
              disabled
              title="Restore requires CA-2 confirmation — coming soon"
              className="w-full text-left px-4 py-2 text-tok-sm text-[rgb(var(--color-text-muted))] opacity-50 cursor-not-allowed"
            >
              Restore Plan
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDialog — generic confirmation modal
// ---------------------------------------------------------------------------

function ConfirmDialog({ title, message, confirmLabel, confirmClass, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
        <div className="px-5 py-4 border-b border-[rgb(var(--color-border))]">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{title}</h3>
        </div>
        <div className="px-5 py-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">{message}</p>
        </div>
        <div className="flex gap-2 px-5 pb-5">
          <button type="button" onClick={onConfirm} className={confirmClass}>
            {confirmLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PlanRow — single plan row. No approve button (AD-42).
// ---------------------------------------------------------------------------

function PlanRow({ plan, roleMap, segmentMap, buMap, onClick, onDelete, onWithdraw, actioning }) {
  const roleName    = roleMap[plan.role_id]?.role_name    ?? roleMap[plan.role_id]?.role_code    ?? `Role ${plan.role_id}`;
  const segmentName = segmentMap[plan.segment_id]?.segment_name ?? segmentMap[plan.segment_id]?.segment_code ?? `Segment ${plan.segment_id}`;
  const buName      = plan.business_unit_id
    ? (buMap[plan.business_unit_id]?.bu_name ?? `BU ${plan.business_unit_id}`)
    : null;

  const isWithdrawn = plan.plan_status === 'withdrawn';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(plan.plan_id, plan.plan_status)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick(plan.plan_id, plan.plan_status)}
      className={[
        'flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))] px-4 py-3 cursor-pointer',
        'hover:bg-[rgb(var(--color-surface-muted))] transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]',
        isWithdrawn ? 'opacity-60' : '',
      ].join(' ')}
    >
      <div className="min-w-0 flex-1 grid gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-tok-sm font-medium text-[rgb(var(--color-text))] truncate">
            {plan.plan_name}
          </span>
          {plan.plan_code && (
            <span className="shrink-0 text-tok-xs text-[rgb(var(--color-text-muted))] font-mono">
              {plan.plan_code}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-tok-xs text-[rgb(var(--color-text-muted))]">
          <span>{segmentName}</span>
          <span>·</span>
          <span>{roleName}</span>
          {buName && <><span>·</span><span>{buName}</span></>}
          {plan.version != null && <><span>·</span><span>v{plan.version}</span></>}
          {plan.assignment_count > 0 && (
            <><span>·</span><span>{plan.assignment_count} position{plan.assignment_count !== 1 ? 's' : ''}</span></>
          )}
        </div>
      </div>
      <div className="ml-4 shrink-0 flex items-center gap-2">
        <div className="flex flex-col items-end gap-0.5">
          <StatusPill status={plan.plan_status} />
          {/* CHANGE-10 S725: sub-status indicators — spec label rules §CHANGE-10 */}
          {(plan.pending_modifier_approval_count > 0) && (
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">
              {plan.pending_modifier_approval_count} awaiting CA-2 approval
            </span>
          )}
          {(plan.required_modifier_missing_count > 0) && (
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">
              {plan.required_modifier_missing_count} required modifier{plan.required_modifier_missing_count !== 1 ? 's' : ''} not yet configured
            </span>
          )}
          {(plan.open_amendment_count > 0) && (
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">
              Amendment in progress
            </span>
          )}
        </div>
        <PlanActionMenu
          plan={plan}
          onDelete={onDelete}
          onWithdraw={onWithdraw}
          actioning={actioning}
        />
        <svg className="h-4 w-4 text-[rgb(var(--color-text-muted))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GroupBlock — collapsible group header + plan rows
// ---------------------------------------------------------------------------

function GroupBlock({ title, subtitle, plans, roleMap, segmentMap, buMap, onPlanClick, onDelete, onWithdraw, actioning, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-[rgb(var(--color-surface-muted))] hover:bg-[rgb(var(--color-surface-muted))]/80 transition-colors focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-4 w-4 text-[rgb(var(--color-text-muted))] transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="text-left">
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">{title}</span>
            {subtitle && (
              <span className="ml-2 text-tok-xs text-[rgb(var(--color-text-muted))]">{subtitle}</span>
            )}
          </div>
        </div>
        <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
        </span>
      </button>
      {open && (
        // S739 Step 6 — scrollable group well: max-height caps at ~6 rows (~384px).
        // Independent scroll context per group — page scroll navigates between groups,
        // well scroll navigates within a group. Enterprise pattern for large plan portfolios.
        <div
          className="p-3 space-y-2 bg-[rgb(var(--color-surface))] overflow-y-auto"
          style={{ maxHeight: '384px' }}
        >
          {plans.map((plan) => (
            <PlanRow
              key={plan.plan_id}
              plan={plan}
              roleMap={roleMap}
              segmentMap={segmentMap}
              buMap={buMap}
              onClick={onPlanClick}
              onDelete={onDelete}
              onWithdraw={onWithdraw}
              actioning={actioning}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// OI-CRL-DESIGN-PLAN-ROLE-FILTER (S622):
// initialRoleId is passed from CompensationPoliciesWorkspace when the "Design Plan"
// button navigates to /admin/compensation-policies?role_id=...&tab=plans.
// It seeds the filterRole dropdown on mount so the user immediately sees plans
// for the role they clicked from the Commercial Role Library.
// PlansLandingPage owns its own filter state after mount — initialRoleId is consumed once.
export default function PlansLandingPage({ period, isLocked: isLockedProp, initialRoleId, onPlanClick }) {
  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  // isLocked may be passed directly from parent; fall back to deriving from period.
  const isLocked = isLockedProp ?? (period?.period_status !== "open");

  const { user } = useAuth();
  const navigate = useNavigate();

  const [plans, setPlans]               = useState([]);
  const [roles, setRoles]               = useState([]);
  const [segments, setSegments]         = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');

  // Fix 6 — debounced search: raw input state drives the input field immediately
  // (so the UI feels responsive), but debouncedSearch drives filtering (300ms delay).
  // Avoids recomputing filtered useMemo on every keystroke across large plan lists.
  const [searchRaw, setSearchRaw]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // §4.6: restore persisted filter state from sessionStorage on mount.
  // sessionStorage is client-side only — no server affinity, stateless-backend compatible.
  const _savedFilters = useMemo(() => {
    try {
      const raw = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PLAN_LIST_FILTER_KEY) : null;
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  }, []); // eslint-disable-line

  // View + filter state — seeded from sessionStorage where available
  const [viewMode, setViewMode]         = useState(_savedFilters.viewMode  ?? 'segment');
  const [filterSegment, setFilterSegment] = useState(_savedFilters.filterSegment ?? '');
  const [filterRole, setFilterRole]     = useState(
    initialRoleId ? String(initialRoleId) : (_savedFilters.filterRole ?? '')
  );
  const [filterBu, setFilterBu]         = useState(_savedFilters.filterBu     ?? '');
  const [filterStatus, setFilterStatus] = useState(_savedFilters.filterStatus ?? '');
  // search state now split: searchRaw (input) / debouncedSearch (filter) — declared above.
  // Seed both from sessionStorage on mount so restored state filters immediately.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _searchSeed = _savedFilters.search ?? '';

  const [showWithdrawn, setShowWithdrawn] = useState(_savedFilters.showWithdrawn ?? false);
  const [sortBy, setSortBy]             = useState(_savedFilters.sortBy ?? 'modified_desc');

  // §4.6 pagination — flat list view only
  const [page, setPage]                 = useState(1);

  // New plan modal
  const [showNewPlan, setShowNewPlan]   = useState(false);
  const [newPlanForm, setNewPlanForm]   = useState({ plan_name: '', plan_code: '', role_id: '', segment_id: '' });
  const [creating, setCreating]         = useState(false);
  const [createError, setCreateError]   = useState('');

  // §19 lifecycle action state
  const [actioning, setActioning]       = useState(null);   // plan_id being actioned
  const [actionError, setActionError]   = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // plan object to confirm delete
  const [confirmWithdraw, setConfirmWithdraw] = useState(null); // plan object to confirm withdraw

  // ---------------------------------------------------------------------------
  // Fix 6 — debounce search input
  // Raw keystroke updates searchRaw immediately (responsive input).
  // debouncedSearch updates after DEBOUNCE_MS — drives filtered useMemo.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(searchRaw), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchRaw]);

  // Seed searchRaw + debouncedSearch from sessionStorage on mount (once only).
  useEffect(() => {
    if (_searchSeed) {
      setSearchRaw(_searchSeed);
      setDebouncedSearch(_searchSeed);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Fix 3 — debounced sessionStorage persist
  // Persists filter state 300ms after last change — avoids per-keystroke writes.
  // §4.6: sessionStorage only — stateless-backend compatible, no server write.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(PLAN_LIST_FILTER_KEY, JSON.stringify({
            viewMode, filterSegment, filterRole, filterBu, filterStatus,
            search: debouncedSearch, showWithdrawn, sortBy,
          }));
        }
      } catch { /* storage quota or private browsing — silently ignore */ }
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [viewMode, filterSegment, filterRole, filterBu, filterStatus, debouncedSearch, showWithdrawn, sortBy]);

  // Reset to page 1 whenever filters or sort change
  useEffect(() => {
    setPage(1);
  }, [filterSegment, filterRole, filterBu, filterStatus, debouncedSearch, showWithdrawn, sortBy]);

  // ---------------------------------------------------------------------------
  // Fix 1+2 — separated reference data load from plan data load
  //
  // BEFORE: single load() fetched plans + roles + segments + BUs together.
  //   Problem: showWithdrawn toggle caused ALL 4-5 endpoints to re-fire,
  //   re-fetching roles/segments/BUs even though they hadn't changed.
  //
  // AFTER: two independent loaders:
  //   loadRefData() — roles, segments, BUs. Fires once on mount (user dep only).
  //                   Never re-fires on plan list interactions.
  //   loadPlans()   — plan data only. Re-fires when user/period/showWithdrawn change.
  //                   Also called explicitly after delete/withdraw mutations.
  // ---------------------------------------------------------------------------

  const loadRefData = useCallback(async () => {
    if (!user) return;
    try {
      const [rolesData, segsData, busData] = await Promise.all([
        apiGet('/api/v1/roles'),
        apiGet('/api/v1/segments'),
        apiGet('/api/v1/business-units'),
      ]);
      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.data ?? rolesData?.roles ?? []);
      setSegments(Array.isArray(segsData) ? segsData : segsData?.data ?? segsData?.segments ?? []);
      setBusinessUnits(Array.isArray(busData) ? busData : busData?.data ?? busData?.business_units ?? []);
    } catch {
      // Reference data failure is non-fatal — plan list still renders with IDs
    }
  }, [user]);

  // Fetch reference data once on mount (or when user changes — login/switch)
  useEffect(() => { loadRefData(); }, [loadRefData]);

  const loadPlans = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const periodId = period?.fiscal_period_id;
      // §19: fetch withdrawn plans separately when toggle is on
      const baseUrl = periodId ? `/api/v1/plans?fiscal_period_id=${periodId}` : '/api/v1/plans';
      const withdrawnUrl = periodId
        ? `/api/v1/plans?fiscal_period_id=${periodId}&plan_status=withdrawn`
        : '/api/v1/plans?plan_status=withdrawn';

      const requests = [apiGet(baseUrl)];
      if (showWithdrawn) requests.push(apiGet(withdrawnUrl));

      const [plansData, withdrawnData] = await Promise.all(requests);

      const activePlans = Array.isArray(plansData) ? plansData : plansData?.data ?? plansData?.plans ?? [];
      const withdrawnPlans = showWithdrawn
        ? (Array.isArray(withdrawnData) ? withdrawnData : withdrawnData?.data ?? withdrawnData?.plans ?? [])
        : [];

      // Merge — deduplicate by plan_id (withdrawn plans may appear in both if backend returns them)
      const allPlansMap = new Map();
      [...activePlans, ...withdrawnPlans].forEach((p) => allPlansMap.set(p.plan_id, p));
      setPlans([...allPlansMap.values()]);
    } catch (err) {
      setError(err?.message || 'Unable to load plans.');
    } finally {
      setLoading(false);
    }
  }, [user, period, showWithdrawn]);

  // Plan data re-fetches when user, period, or showWithdrawn changes
  useEffect(() => { loadPlans(); }, [loadPlans]);

  // Alias load → loadPlans for mutation callbacks (handleDeletePlan, handleWithdrawPlan)
  const load = loadPlans;

  // ---------------------------------------------------------------------------
  // Lookup maps
  // ---------------------------------------------------------------------------

  const roleMap    = useMemo(() => Object.fromEntries(roles.map((r) => [r.role_id, r])), [roles]);
  const segmentMap = useMemo(() => Object.fromEntries(segments.map((s) => [s.segment_id, s])), [segments]);
  const buMap      = useMemo(() => Object.fromEntries(businessUnits.map((b) => [b.business_unit_id, b])), [businessUnits]);

  // BU toggle disabled when no plans have BU attribution (Polaris case — spec §4.4)
  const anyPlanHasBu = useMemo(() => plans.some((p) => p.business_unit_id != null), [plans]);

  // ---------------------------------------------------------------------------
  // Filter + search
  // ---------------------------------------------------------------------------

  const filtered = useMemo(() => {
    let result = plans;
    // If showWithdrawn is off, exclude withdrawn from display
    if (!showWithdrawn) result = result.filter((p) => p.plan_status !== 'withdrawn');
    if (filterSegment) result = result.filter((p) => String(p.segment_id) === filterSegment);
    if (filterRole)    result = result.filter((p) => String(p.role_id) === filterRole);
    if (filterBu)      result = result.filter((p) => String(p.business_unit_id) === filterBu);
    if (filterStatus)  result = result.filter((p) => p.plan_status === filterStatus);
    // Fix 6: use debouncedSearch (not raw input) — avoids recompute on every keystroke
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      result = result.filter((p) =>
        (p.plan_name  ?? '').toLowerCase().includes(q) ||
        (p.plan_code  ?? '').toLowerCase().includes(q)
      );
    }
    // §4.6 sort
    const sorted = [...result];
    if (sortBy === 'name_asc')      sorted.sort((a, b) => (a.plan_name ?? '').localeCompare(b.plan_name ?? ''));
    else if (sortBy === 'name_desc') sorted.sort((a, b) => (b.plan_name ?? '').localeCompare(a.plan_name ?? ''));
    else if (sortBy === 'modified_asc')  sorted.sort((a, b) => (a.updated_at ?? a.created_at ?? '').localeCompare(b.updated_at ?? b.created_at ?? ''));
    else if (sortBy === 'modified_desc') sorted.sort((a, b) => (b.updated_at ?? b.created_at ?? '').localeCompare(a.updated_at ?? a.created_at ?? ''));
    else if (sortBy === 'status') sorted.sort((a, b) =>
      (STATUS_ATTENTION_ORDER[a.plan_status] ?? 99) - (STATUS_ATTENTION_ORDER[b.plan_status] ?? 99)
    );
    return sorted;
  }, [plans, filterSegment, filterRole, filterBu, filterStatus, debouncedSearch, showWithdrawn, sortBy]);

  // §4.6 pagination — flat list only (grouped views are self-contained per group)
  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const paginated   = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ---------------------------------------------------------------------------
  // Group by view mode
  // ---------------------------------------------------------------------------

  const grouped = useMemo(() => {
    if (viewMode === 'flat') return null;

    const groups = {};

    if (viewMode === 'segment') {
      filtered.forEach((p) => {
        const seg = segmentMap[p.segment_id];
        const key = String(p.segment_id);
        const title = seg?.segment_name ?? seg?.segment_code ?? `Segment ${p.segment_id}`;
        if (!groups[key]) groups[key] = { title, subtitle: seg?.segment_code, plans: [] };
        groups[key].plans.push(p);
      });
    } else if (viewMode === 'role') {
      filtered.forEach((p) => {
        const role = roleMap[p.role_id];
        const key = String(p.role_id);
        const title = role?.role_name ?? role?.role_code ?? `Role ${p.role_id}`;
        if (!groups[key]) groups[key] = { title, subtitle: role?.role_category, plans: [] };
        groups[key].plans.push(p);
      });
    } else if (viewMode === 'bu') {
      filtered.forEach((p) => {
        const key = p.business_unit_id ? String(p.business_unit_id) : '__null__';
        const bu = buMap[p.business_unit_id];
        const title = bu ? (bu.bu_name ?? bu.bu_code) : 'No Business Unit Assigned';
        if (!groups[key]) groups[key] = { title, plans: [] };
        groups[key].plans.push(p);
      });
    } else if (viewMode === 'status') {
      filtered.forEach((p) => {
        const key = p.plan_status ?? 'unknown';
        const title = STATUS_LABELS[key] ?? key;
        if (!groups[key]) groups[key] = { title, plans: [] };
        groups[key].plans.push(p);
      });
    }

    return Object.entries(groups).sort(([, a], [, b]) => a.title.localeCompare(b.title));
  }, [filtered, viewMode, segmentMap, roleMap, buMap]);

  // ---------------------------------------------------------------------------
  // New plan
  // ---------------------------------------------------------------------------

  const handleCreatePlan = useCallback(async () => {
    if (!newPlanForm.plan_name.trim() || !newPlanForm.plan_code.trim() ||
        !newPlanForm.role_id || !newPlanForm.segment_id) return;
    setCreating(true);
    setCreateError('');
    try {
      const body = {
        plan_name:        newPlanForm.plan_name.trim(),
        plan_code:        newPlanForm.plan_code.trim().toUpperCase(),
        role_id:          parseInt(newPlanForm.role_id, 10),
        segment_id:       parseInt(newPlanForm.segment_id, 10),
        fiscal_period_id: period?.fiscal_period_id ?? undefined,
      };
      const result = await apiPost('/api/v1/plans', body);
      const planId = result?.plan_id ?? result?.data?.plan_id;
      setShowNewPlan(false);
      setNewPlanForm({ plan_name: '', plan_code: '', role_id: '', segment_id: '' });
      if (planId && onPlanClick) onPlanClick(planId);
      else if (planId) navigate(`/admin/plans/${planId}`);
      else load();
    } catch (err) {
      // C12 — S689: wizard_session 422 recovery (mastercontext AD-57).
      // POST /api/v1/plans returns 422 WIZARD_CONTEXT_REQUIRED when no active
      // wizard_session exists for the tenant. This is tenant-scoped (not user-scoped)
      // — any comp_admin can unblock by completing the onboarding wizard.
      const errCode = err?.code || err?.detail?.error?.code || '';
      const errMsg  = err?.message || '';
      const isWizardGate = errCode === 'WIZARD_CONTEXT_REQUIRED' ||
                           errMsg.includes('WIZARD_CONTEXT_REQUIRED') ||
                           errMsg.includes('wizard') ||
                           (err?.status === 422 && errMsg.includes('session'));
      if (isWizardGate) {
        setCreateError(
          'Plan creation requires an active onboarding session. ' +
          'Complete the setup wizard first, or ask your administrator to activate it.'
        );
      } else {
        setCreateError(errMsg || 'Unable to create plan.');
      }
    } finally {
      setCreating(false);
    }
  }, [newPlanForm, period, navigate, load, onPlanClick]);

  const handlePlanClick = useCallback((planId, planStatus) => {
    if (onPlanClick) onPlanClick(planId, planStatus);
    else navigate(`/admin/plans/${planId}`);
  }, [onPlanClick, navigate]);

  // ---------------------------------------------------------------------------
  // §19 Lifecycle actions
  // ---------------------------------------------------------------------------

  const handleDeletePlan = useCallback(async (plan) => {
    setActioning(plan.plan_id);
    setActionError('');
    try {
      await apiDelete(`/api/v1/plans/${plan.plan_id}`);
      await load();
    } catch (err) {
      setActionError(err?.message || 'Failed to delete plan.');
    } finally {
      setActioning(null);
      setConfirmDelete(null);
    }
  }, [load]);

  const handleWithdrawPlan = useCallback(async (plan) => {
    setActioning(plan.plan_id);
    setActionError('');
    try {
      await apiPost(`/api/v1/plans/${plan.plan_id}/withdraw`, {});
      await load();
    } catch (err) {
      setActionError(err?.message || 'Failed to withdraw plan.');
    } finally {
      setActioning(null);
      setConfirmWithdraw(null);
    }
  }, [load]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const hasActiveFilters = filterSegment || filterRole || filterBu || filterStatus || searchRaw.trim();

  return (
    <section className="flex flex-col gap-4">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Plans</h2>
          <p className="mt-0.5 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Configure compensation plans and their modifier components.
            {period && <span> · {period.period_label ?? period.period_code}</span>}
          </p>
        </div>
        {isLocked ? (
          <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5">
            <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
            <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status}</span>
          </div>
        ) : (
          <button type="button" className={btnPrimary} onClick={() => setShowNewPlan(true)}>
            New Plan
          </button>
        )}
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3">
          <p className="text-tok-sm text-[rgb(var(--color-danger))]">{actionError}</p>
          <button
            type="button"
            onClick={() => setActionError('')}
            className="ml-4 text-tok-xs text-[rgb(var(--color-danger))] underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-1 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-1 w-fit">
        {VIEW_MODES.map((vm) => {
          const isDisabled = vm.key === 'bu' && !anyPlanHasBu;
          return (
            <button
              key={vm.key}
              type="button"
              disabled={isDisabled}
              title={isDisabled ? 'Business Unit view unavailable — no plans currently have BU attribution.' : undefined}
              onClick={() => !isDisabled && setViewMode(vm.key)}
              className={[
                'rounded-lg px-3 py-1.5 text-tok-xs font-medium transition-colors',
                viewMode === vm.key
                  ? 'bg-[rgb(var(--color-primary))] text-white'
                  : isDisabled
                  ? 'text-[rgb(var(--color-text-muted))] opacity-40 cursor-not-allowed'
                  : 'text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] hover:text-[rgb(var(--color-text))]',
              ].join(' ')}
            >
              {vm.label}
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search plans…"
          value={searchRaw}
          onChange={(e) => setSearchRaw(e.target.value)}
          className={inputClass + ' w-48'}
        />
        <select value={filterSegment} onChange={(e) => setFilterSegment(e.target.value)} className={inputClass}>
          <option value="">All segments</option>
          {segments.map((s) => (
            <option key={s.segment_id} value={s.segment_id}>
              {s.segment_name ?? s.segment_code}
            </option>
          ))}
        </select>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className={inputClass}>
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.role_id} value={r.role_id}>
              {r.role_name ?? r.role_code}
            </option>
          ))}
        </select>
        {anyPlanHasBu && (
          <select value={filterBu} onChange={(e) => setFilterBu(e.target.value)} className={inputClass}>
            <option value="">All business units</option>
            {businessUnits.map((b) => (
              <option key={b.business_unit_id} value={b.business_unit_id}>
                {b.bu_name ?? b.bu_code}
              </option>
            ))}
          </select>
        )}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={inputClass}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* §4.6 Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className={inputClass}
          aria-label="Sort plans"
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        {/* §19 Show withdrawn toggle */}
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showWithdrawn}
            onChange={(e) => setShowWithdrawn(e.target.checked)}
            className="rounded border-[rgb(var(--color-border))] text-[rgb(var(--color-primary))] focus:ring-[rgb(var(--color-focus-ring))]"
          />
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Show withdrawn</span>
        </label>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => { setFilterSegment(''); setFilterRole(''); setFilterBu(''); setFilterStatus(''); setSearchRaw(''); setDebouncedSearch(''); }}
            className="text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Content */}
      {loading && (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {error}
        </div>
      )}

      {!loading && !error && plans.length === 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-10 text-center">
          <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No plans yet</p>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Create a plan to begin configuring compensation components and modifiers.
          </p>
          {isLocked ? (
            <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5 mt-4">
              <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
              <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status} — switch to an open period to create plans</span>
            </div>
          ) : (
            <button type="button" className={btnPrimary + ' mt-4'} onClick={() => setShowNewPlan(true)}>
              New Plan
            </button>
          )}
        </div>
      )}

      {!loading && !error && plans.length > 0 && filtered.length === 0 && (
        <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-6 py-8 text-center">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No plans match the current filters.</p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          {/* Grouped view */}
          {grouped && (
            <div className="space-y-3">
              {grouped.map(([key, group]) => (
                <GroupBlock
                  key={key}
                  title={group.title}
                  subtitle={group.subtitle}
                  plans={group.plans}
                  roleMap={roleMap}
                  segmentMap={segmentMap}
                  buMap={buMap}
                  onPlanClick={handlePlanClick}
                  onDelete={(plan) => setConfirmDelete(plan)}
                  onWithdraw={(plan) => setConfirmWithdraw(plan)}
                  actioning={actioning}
                />
              ))}
            </div>
          )}

          {/* Flat list view — paginated (§4.6) */}
          {!grouped && (
            <div className="space-y-2">
              {paginated.map((plan) => (
                <PlanRow
                  key={plan.plan_id}
                  plan={plan}
                  roleMap={roleMap}
                  segmentMap={segmentMap}
                  buMap={buMap}
                  onClick={handlePlanClick}
                  onDelete={(plan) => setConfirmDelete(plan)}
                  onWithdraw={(plan) => setConfirmWithdraw(plan)}
                  actioning={actioning}
                />
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              {filtered.length} of {plans.length} plan{plans.length !== 1 ? 's' : ''}
              {showWithdrawn && ' (including withdrawn)'}
            </p>
            {/* §4.6 pagination — flat list only */}
            {!grouped && totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
                >
                  ‹ Prev
                </button>
                <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                  {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="rounded-lg border border-[rgb(var(--color-border))] px-2.5 py-1 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
                >
                  Next ›
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* §19 Delete confirmation dialog */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete Plan"
          message={
            confirmDelete.component_count === 0
              ? `Delete "${confirmDelete.plan_name}"? This plan has no components and has never been submitted for approval. This cannot be undone.`
              : `Delete "${confirmDelete.plan_name}"? This will permanently delete the plan and all ${confirmDelete.component_count} configured component${confirmDelete.component_count !== 1 ? 's' : ''}. This cannot be undone.`
          }
          confirmLabel={actioning === confirmDelete.plan_id ? 'Deleting…' : 'Delete Plan'}
          confirmClass={btnDanger}
          onConfirm={() => handleDeletePlan(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* §19 Withdraw confirmation dialog */}
      {confirmWithdraw && (
        <ConfirmDialog
          title="Withdraw Plan"
          message={`Withdraw "${confirmWithdraw.plan_name}"? The plan will be removed from the active plan list and will no longer be available for new assignments. No positions are currently assigned to this plan. This action can be reversed by a second approver.`}
          confirmLabel={actioning === confirmWithdraw.plan_id ? 'Withdrawing…' : 'Withdraw Plan'}
          confirmClass="rounded-lg bg-[rgb(var(--color-warning))] px-3 py-1.5 text-tok-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          onConfirm={() => handleWithdrawPlan(confirmWithdraw)}
          onCancel={() => setConfirmWithdraw(null)}
        />
      )}

      {/* New Plan Modal */}
      {showNewPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4">
              <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">New Plan</h3>
              <button
                type="button"
                onClick={() => { setShowNewPlan(false); setCreateError(''); }}
                className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Plan Name <span className="text-[rgb(var(--color-danger))]">*</span>
                </span>
                <input
                  type="text"
                  value={newPlanForm.plan_name}
                  onChange={(e) => setNewPlanForm((f) => ({ ...f, plan_name: e.target.value }))}
                  placeholder="e.g. Enterprise AE FY2026"
                  className={inputClass + ' mt-1 w-full'}
                />
              </label>
              <label className="block">
                <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Plan Code <span className="text-[rgb(var(--color-danger))]">*</span>
                </span>
                <input
                  type="text"
                  value={newPlanForm.plan_code}
                  onChange={(e) => setNewPlanForm((f) => ({ ...f, plan_code: e.target.value.toUpperCase() }))}
                  placeholder="ENT-AE-FY26"
                  className={inputClass + ' mt-1 w-full font-mono'}
                />
              </label>
              <label className="block">
                <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Role <span className="text-[rgb(var(--color-danger))]">*</span>
                </span>
                <select
                  value={newPlanForm.role_id}
                  onChange={(e) => setNewPlanForm((f) => ({ ...f, role_id: e.target.value }))}
                  className={inputClass + ' mt-1 w-full'}
                >
                  <option value="">Select role…</option>
                  {roles.filter((r) => r.is_active !== false).map((r) => (
                    <option key={r.role_id} value={r.role_id}>{r.role_name ?? r.role_code}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
                  Segment <span className="text-[rgb(var(--color-danger))]">*</span>
                </span>
                <select
                  value={newPlanForm.segment_id}
                  onChange={(e) => setNewPlanForm((f) => ({ ...f, segment_id: e.target.value }))}
                  className={inputClass + ' mt-1 w-full'}
                >
                  <option value="">Select segment…</option>
                  {segments.filter((s) => s.is_active !== false).map((s) => (
                    <option key={s.segment_id} value={s.segment_id}>{s.segment_name ?? s.segment_code}</option>
                  ))}
                </select>
              </label>
              {createError && (
                <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
                  {createError}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCreatePlan}
                  disabled={creating || !newPlanForm.plan_name.trim() || !newPlanForm.plan_code.trim() || !newPlanForm.role_id || !newPlanForm.segment_id}
                  className={btnPrimary}
                >
                  {creating ? 'Creating…' : 'Create Plan'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowNewPlan(false); setCreateError(''); }}
                  className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
